import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Img, { ImgProps } from "@insite/mobius/Img";
import { Hidden } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ThumbnailVideoIcon from "@NA/Icons/ThumbnailVideoIcon";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import { getUpdatedImages } from "@NA/Utils/Utility";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    selectedIndex?: number;
    inModal?: boolean;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
    setFullImageModalIsOpen?: (item: boolean) => void;
}
type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
const mapStateToProps = (state: CustomApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    selectedmvpImage: state.ADIGlobalData.updatePDPTab.selectedMVPImage,
});

const mapDispatchToProps = {
    
};

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: ImgProps;
    selectedImage?: ImgProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    thumbnailIcon?: InjectableCss;
    indicator?: InjectableCss;
}

const getImageCss = (borderColor: string, borderSize: number) => css`
    width: 100%;
    height: 100%;    
    border: ${borderSize}px solid ${getColor(borderColor)};
    cursor: pointer;
    justify-content: flex-start;
    img {
        width: 64px;
        height: 64px;
        padding: 3px;
    }
`;

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: { width: 12 },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            padding-left: 17px;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            gap: 18px;
            flex-wrap: wrap;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 ${100 / 6}%;
            padding: 0;
            position: relative;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 64px;
            height:64px;
            text-align: center;
            position: relative;
            // padding: 10px 3px 4px;
            > button {
                position: relative;
                width: 64px;
                height:64px;
            }
        `,
    },
    image: {
        css: getImageCss("common.border", 1),
    },
    selectedImage: {
        css: getImageCss(`${ADIColors.lightblue2}`, 2),
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    thumbnailIcon: {
        css: css`
            position: absolute;
            z-index: 9;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
                width: 46px;
                height: 46px;
            }
        `,
    },
    indicator: {
        css: css`
            position: absolute;
            z-index: 9;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            background: rgba(0, 0, 0, 0.4);
            color: #fff;
            font-size: 30px;
            font-weight: bold;
        `,
    },
};

const ProductImageCarousel = ({
    productSettings,
    productContext,
    selectedIndex,
    onSelectImage,
    extendedStyles,
    inModal = false,
    setFullImageModalIsOpen,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;
    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    const [isTabView, setIsTabView] = React.useState(false);
    const [multiVarientImages, setMultiVarientImages] = useState<ImageModel[]>([]);    
    const mvpvideoLink1 = productContext?.product?.properties?.video1Url;
    const mvpvideoLink2 = productContext?.product?.properties?.video2Url;
    const thumbnailBg1 = productContext?.product?.properties?.thumbnail1Url;
    const thumbnailBg2 = productContext?.product?.properties?.thumbnail2Url;
    const imagesData = productContext?.product?.images || [];

    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const dispatch = useDispatch();
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };
    
    React.useEffect(() => {
        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
        
    }, []);
    const onWindowResize = () => {
        if (window.innerWidth >= 991) {
            setIsTabView(false);
        } else {
            setIsTabView(true);
        }
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
    }, [embla]);

    useEffect(() => {
        if (!embla || !product.images) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll: 1,
            draggable: false,
        });

        setCanScroll();
    }, [embla, product]);

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "video") {
            return productImage.smallImagePath;
        }
        if (productImage.imageType === "Static") {            
            if(productImage.name === "AlternateImage"){
                return productImage.largeImagePath;    
            }
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product || !product.images || product.images?.length === 0 || (isTabView && !inModal)) {
        return null;
    } 
    
        const isMultiVariantProduct = !!(
        product?.variantTypeId?.length && product?.variantTypeId !== null
        );
    
        const getMultiVariantImages = React.useCallback(() => {
        if (!isMultiVariantProduct) return;        
            const updatedImages = getUpdatedImages(productContext?.product);           
    
            setMultiVarientImages((prevImages) => {
                const areImagesEqual =
                JSON.stringify(prevImages) === JSON.stringify(updatedImages);
                return areImagesEqual ? prevImages : updatedImages;
            });
        }, [isMultiVariantProduct, imagesData, mvpvideoLink1, mvpvideoLink2, thumbnailBg1, thumbnailBg2, product.id]);
    
        useEffect(() => {
            getMultiVariantImages();            
        }, [getMultiVariantImages]);
    
        useEffect(() => {
            
        }, [multiVarientImages]);
        

        useEffect(() => {
            setSelectedImageIndex(selectedIndex);
        }, [selectedIndex]);        
    
        const imageClickHandler = (index: number) => {
            if (!isMultiVariantProduct) {
                setSelectedImageIndex(index);        
                onSelectImage?.(index);                              
            } else {                
                const selectedmvpImage = multiVarientImages.find((image) => image.id === multiVarientImages[index].id);                
                dispatch({
                    type: "Data/PDP/selectedMVPImage",
                    result: selectedmvpImage
                })
            }            
        };        

        const multiVariantImageRender = () => {
            if(multiVarientImages.length && multiVarientImages?.length > 1 && isMultiVariantProduct) {
                return (
                    <>
                        <GridItem {...styles.carouselGridItem}>
                            <StyledWrapper {...styles.carouselWrapper}>
                                <StyledWrapper {...styles.carouselContainer}>
                                    <StyledWrapper {...styles.carouselSlidesContainer}>
                                        {(multiVarientImages) 
                                            .slice(0, !inModal ? 4 : multiVarientImages.length)
                                            .map((productImage, index) => (
                                                <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                                                    <StyledWrapper {...styles.carouselSlideInner}>
                                                        <Clickable
                                                            onClick={() => {
                                                                if (
                                                                    !inModal &&
                                                                    multiVarientImages?.length &&
                                                                    multiVarientImages?.length > 4 &&
                                                                    index === 3
                                                                ) {
                                                                    setFullImageModalIsOpen?.(true);
                                                                    return;
                                                                }
                                                                imageClickHandler(index);
                                                            }}
                                                        >
                                                            {productImage.imageType === "video" ? (
                                                                <StyledWrapper {...styles.thumbnailIcon}>
                                                                    <ThumbnailVideoIcon />
                                                                </StyledWrapper>
                                                            ) : null}
                                                            {!inModal &&
                                                            multiVarientImages?.length &&
                                                            multiVarientImages?.length > 4 &&
                                                            index === 3 ? (
                                                                <StyledWrapper {...styles.indicator}>
                                                                    +{multiVarientImages?.length - 4}
                                                                </StyledWrapper>
                                                            ) : null}
                                                            {multiVarientImages?.length && multiVarientImages?.length > 1 &&
                                                            <Img
                                                                {...(index === selectedImageIndex
                                                                    ? styles.selectedImage
                                                                    : styles.image)}
                                                                loading="eager"
                                                                src={getProductImageThumbPath(productImage)}
                                                                altText={productImage.imageAltText}
                                                            />
                                                            }
                                                        </Clickable>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            ))}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    </>
                )
            } 
        }
    

    return (
        <GridContainer {...styles.mainContainer} key={product.id}>
            { isMultiVariantProduct ? (
                multiVariantImageRender()
            ) : product.images.length && product.images?.length > 1 && !isMultiVariantProduct  && (
                <>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer}>
                            <StyledWrapper {...styles.carouselSlidesContainer}>
                                {product?.images
                                    .slice(0, !inModal ? 4 : product.images.length)
                                    .map((productImage, index) => (
                                        <StyledWrapper {...styles.carouselSlide} key={productImage.id}>
                                            <StyledWrapper {...styles.carouselSlideInner}>
                                                <Clickable
                                                    onClick={() => {
                                                        if (
                                                            !inModal &&
                                                            product.images?.length &&
                                                            product.images?.length > 4 &&
                                                            index === 3
                                                        ) {
                                                            setFullImageModalIsOpen?.(true);
                                                            return;
                                                        }
                                                        imageClickHandler(index);
                                                    }}
                                                >
                                                    {productImage.imageType === "video" ? (
                                                        <StyledWrapper {...styles.thumbnailIcon}>
                                                            <ThumbnailVideoIcon />
                                                        </StyledWrapper>
                                                    ) : null}
                                                    {!inModal &&
                                                    product.images?.length &&
                                                    product.images?.length > 4 &&
                                                    index === 3 ? (
                                                        <StyledWrapper {...styles.indicator}>
                                                            +{product.images.length - 4}
                                                        </StyledWrapper>
                                                    ) : null}
                                                    {product.images?.length && product.images?.length > 1 &&
                                                    <Img
                                                        {...(index === selectedImageIndex
                                                            ? styles.selectedImage
                                                            : styles.image)}
                                                        loading="eager"
                                                        src={getProductImageThumbPath(productImage)}
                                                        altText={productImage.imageAltText}
                                                    />
                                                    }
                                                </Clickable>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                </>
                )}            
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductImageCarousel));
